import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminGetDashboard } from '../../lib/adminApi';
import AdminSidebar from '../../components/AdminSidebar';

const NAVY   = '#0a2540';
const ORANGE = '#F37428';
const BLUE   = '#0176CE';

const PRESETS = ['Today', '7D', '30D', '90D', 'YTD', 'All'];

function fmt$(n) { return n == null ? '—' : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmtDate(raw) { if (!raw) return '—'; return new Date(raw).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
function toISODate(d) { return d.toISOString().slice(0, 10); }

function calcRange(preset) {
  const now = new Date();
  const today = toISODate(now);
  if (preset === 'Today') return { from: today, to: today };
  if (preset === '7D')    { const d = new Date(now); d.setDate(d.getDate() - 6);  return { from: toISODate(d), to: today }; }
  if (preset === '30D')   { const d = new Date(now); d.setDate(d.getDate() - 29); return { from: toISODate(d), to: today }; }
  if (preset === '90D')   { const d = new Date(now); d.setDate(d.getDate() - 89); return { from: toISODate(d), to: today }; }
  if (preset === 'YTD')   { return { from: `${now.getFullYear()}-01-01`, to: today }; }
  return { from: '', to: '' }; // All
}

function StatusBadge({ status }) {
  const cfg = {
    complete:   { color: '#059669', bg: 'rgba(5,150,105,0.08)'   },
    processing: { color: BLUE,      bg: 'rgba(1,118,206,0.08)'   },
    pending:    { color: '#b45309', bg: 'rgba(245,158,11,0.08)'  },
    failed:     { color: '#dc2626', bg: 'rgba(239,68,68,0.08)'   },
    uploaded:   { color: '#64748b', bg: 'rgba(100,116,139,0.08)' },
  };
  const c = cfg[status?.toLowerCase()] || cfg.uploaded;
  return <span style={{ background: c.bg, color: c.color, borderRadius: 5, fontSize: 10, fontWeight: 600, padding: '2px 8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{status || '—'}</span>;
}

function StatCard({ label, value, sub, color, selected, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: '#fff',
        border: `1px solid ${selected ? color : '#e2e8f0'}`,
        borderRadius: 12,
        borderTop: `4px solid ${color}`,
        padding: '20px 24px',
        boxShadow: selected ? `0 0 0 2px ${color}33` : '0 1px 3px rgba(0,0,0,0.05)',
        cursor: 'pointer',
        transition: 'box-shadow 0.15s, border-color 0.15s',
      }}
    >
      <div style={{ fontSize: 26, fontWeight: 700, color, letterSpacing: '-0.5px', marginBottom: 2 }}>{value}</div>
      <div style={{ color: '#64748b', fontSize: 12, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      {sub && <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function DrillDown({ card, data }) {
  if (!card || !data) return null;

  const wrapStyle = {
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    marginBottom: 24,
  };

  if (card === 'New Tenants') {
    const list = data.tenants?.new_list || [];
    return (
      <div style={wrapStyle}>
        <div style={s.cardHead}><span style={s.cardTitle}>New Tenants in Period</span></div>
        <table style={s.table}>
          <thead><tr>{['Company', 'Email', 'Plan', 'Joined'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={4} style={{ ...s.td, color: '#94a3b8', textAlign: 'center', padding: 24 }}>No new tenants in this period</td></tr>}
            {list.map((t, i) => (
              <tr key={i} style={s.tr}>
                <td style={{ ...s.td, fontWeight: 500, color: NAVY }}>{t.company_name || '—'}</td>
                <td style={{ ...s.td, color: '#64748b', fontSize: 12 }}>{t.email || '—'}</td>
                <td style={s.td}>{t.plan || 'free'}</td>
                <td style={{ ...s.td, color: '#94a3b8', fontSize: 12 }}>{fmtDate(t.created_on_utc)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (card === 'Revenue') {
    const payments = data.revenue?.payments || data.recent_jobs || [];
    return (
      <div style={wrapStyle}>
        <div style={s.cardHead}><span style={s.cardTitle}>Recent Payments</span></div>
        <table style={s.table}>
          <thead><tr>{['Tenant', 'File', 'Amount', 'Date'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
          <tbody>
            {payments.length === 0 && <tr><td colSpan={4} style={{ ...s.td, color: '#94a3b8', textAlign: 'center', padding: 24 }}>No payment data</td></tr>}
            {payments.map((p, i) => (
              <tr key={i} style={s.tr}>
                <td style={{ ...s.td, fontWeight: 500, color: NAVY }}>{p.company_name || '—'}</td>
                <td style={{ ...s.td, color: '#64748b', fontSize: 12 }}>{p.file_name || '—'}</td>
                <td style={{ ...s.td, color: '#059669', fontWeight: 600 }}>{fmt$(p.amount_usd)}</td>
                <td style={{ ...s.td, color: '#94a3b8', fontSize: 12 }}>{fmtDate(p.created_on_utc)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (card === 'Cost') {
    const breakdown = data.cost_breakdown || [];
    return (
      <div style={wrapStyle}>
        <div style={s.cardHead}><span style={s.cardTitle}>Cost Breakdown</span></div>
        <table style={s.table}>
          <thead><tr>{['Source', 'Records', 'Tokens', 'Estimated Cost'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
          <tbody>
            {breakdown.length === 0 && <tr><td colSpan={4} style={{ ...s.td, color: '#94a3b8', textAlign: 'center', padding: 24 }}>No cost data</td></tr>}
            {breakdown.map((row, i) => (
              <tr key={i} style={s.tr}>
                <td style={{ ...s.td, fontWeight: 500, color: NAVY }}>{row.source || '—'}</td>
                <td style={s.td}>{(row.count || 0).toLocaleString()}</td>
                <td style={s.td}>{(row.tokens || 0).toLocaleString()}</td>
                <td style={{ ...s.td, color: '#059669', fontWeight: 600 }}>{fmt$(row.cost)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (card === 'Jobs Incomplete') {
    const incomplete = data.jobs?.incomplete_list || [];
    return (
      <div style={wrapStyle}>
        <div style={s.cardHead}><span style={s.cardTitle}>Incomplete Jobs</span></div>
        <table style={s.table}>
          <thead><tr>{['Tenant', 'File', 'Records', 'Status', 'Submitted'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
          <tbody>
            {incomplete.length === 0 && <tr><td colSpan={5} style={{ ...s.td, color: '#94a3b8', textAlign: 'center', padding: 24 }}>No incomplete jobs</td></tr>}
            {incomplete.map((j, i) => (
              <tr key={i} style={s.tr}>
                <td style={{ ...s.td, fontWeight: 500, color: NAVY }}>{j.company_name || '—'}</td>
                <td style={{ ...s.td, color: '#64748b', fontSize: 12 }}>{j.file_name || '—'}</td>
                <td style={s.td}>{(j.record_count || 0).toLocaleString()}</td>
                <td style={s.td}><StatusBadge status={j.status} /></td>
                <td style={{ ...s.td, color: '#94a3b8', fontSize: 12 }}>{fmtDate(j.created_on_utc)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return null;
}

export default function AdminDashboard() {
  const navigate = useNavigate();

  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [selectedCard, setSelectedCard] = useState(null);
  const [dateRange, setDateRange] = useState(() => {
    const { from, to } = calcRange('30D');
    return { from, to, preset: '30D' };
  });

  useEffect(() => {
    setLoading(true);
    setError('');
    const params = {};
    if (dateRange.from) params.from = dateRange.from;
    if (dateRange.to)   params.to   = dateRange.to;
    adminGetDashboard(params)
      .then(res => { setData(res); setLoading(false); })
      .catch(e  => { setError(e.message); setLoading(false); });
  }, [dateRange]);

  function handlePreset(preset) {
    const { from, to } = calcRange(preset);
    setDateRange({ from, to, preset });
    setSelectedCard(null);
  }

  function handleCustomFrom(e) {
    setDateRange(prev => ({ ...prev, from: e.target.value, preset: 'custom' }));
    setSelectedCard(null);
  }

  function handleCustomTo(e) {
    setDateRange(prev => ({ ...prev, to: e.target.value, preset: 'custom' }));
    setSelectedCard(null);
  }

  function toggleCard(name) {
    setSelectedCard(prev => prev === name ? null : name);
  }

  const d = data || {};

  const row1 = [
    { label: 'New Tenants',  value: d.tenants?.new_in_period ?? '—', sub: `${d.tenants?.total ?? 0} total`,           color: BLUE   },
    { label: 'Revenue',      value: fmt$(d.revenue?.total_revenue),   sub: `${d.revenue?.payment_count ?? 0} payments`, color: ORANGE },
    { label: 'Cost',         value: fmt$(d.revenue?.total_cost),      sub: 'API & compute',                             color: BLUE   },
    { label: 'Profit',       value: fmt$(d.revenue?.total_profit),    sub: 'Revenue minus cost',                        color: ORANGE },
  ];

  const row2 = [
    { label: 'Jobs Uploaded',   value: d.jobs?.uploaded_in_period ?? d.jobs?.total ?? '—', sub: `${d.jobs?.total ?? 0} all time`,      color: BLUE   },
    { label: 'Jobs Completed',  value: d.jobs?.complete ?? '—',                             sub: 'Successfully processed',             color: ORANGE },
    { label: 'Jobs Incomplete', value: d.jobs?.incomplete ?? '—',                           sub: 'Pending or failed',                  color: BLUE   },
    { label: 'Records Enriched',value: (d.records?.enriched ?? 0).toLocaleString(),         sub: `${(d.records?.uploaded ?? 0).toLocaleString()} uploaded`, color: ORANGE },
  ];

  return (
    <div style={s.page}>
      <AdminSidebar active="dashboard" />
      <main style={s.main}>
        {/* Header */}
        <div style={s.header}>
          <div>
            <h1 style={s.title}>Dashboard</h1>
            <p style={s.sub}>Platform overview</p>
          </div>
        </div>

        {/* Date range controls */}
        <div style={s.rangeBar}>
          <div style={s.presets}>
            {PRESETS.map(p => (
              <button
                key={p}
                onClick={() => handlePreset(p)}
                style={{
                  ...s.presetBtn,
                  background: dateRange.preset === p ? NAVY : '#fff',
                  color:      dateRange.preset === p ? '#fff' : '#374151',
                  border:     `1px solid ${dateRange.preset === p ? NAVY : '#e2e8f0'}`,
                }}
              >{p}</button>
            ))}
          </div>
          <div style={s.customDates}>
            <span style={s.dateLabel}>From</span>
            <input type="date" style={s.dateInput} value={dateRange.from} onChange={handleCustomFrom} />
            <span style={s.dateLabel}>To</span>
            <input type="date" style={s.dateInput} value={dateRange.to}   onChange={handleCustomTo} />
          </div>
        </div>

        {loading && <div style={{ color: '#64748b', padding: 32 }}>Loading…</div>}
        {error   && <div style={{ color: '#ef4444', padding: 32 }}>{error}</div>}

        {!loading && !error && (
          <>
            {/* Row 1 stat cards */}
            <div style={s.statsRow}>
              {row1.map(({ label, value, sub, color }) => (
                <StatCard
                  key={label}
                  label={label}
                  value={value}
                  sub={sub}
                  color={color}
                  selected={selectedCard === label}
                  onClick={() => toggleCard(label)}
                />
              ))}
            </div>

            {/* Row 2 stat cards */}
            <div style={{ ...s.statsRow, marginBottom: 20 }}>
              {row2.map(({ label, value, sub, color }) => (
                <StatCard
                  key={label}
                  label={label}
                  value={value}
                  sub={sub}
                  color={color}
                  selected={selectedCard === label}
                  onClick={() => toggleCard(label)}
                />
              ))}
            </div>

            {/* Drill-down */}
            <DrillDown card={selectedCard} data={d} />

            {/* Recent Jobs */}
            <div style={s.card}>
              <div style={s.cardHead}>
                <span style={s.cardTitle}>Recent Jobs</span>
                <button style={s.viewBtn} onClick={() => navigate('/admin/tenants')}>View all tenants →</button>
              </div>
              <table style={s.table}>
                <thead>
                  <tr>{['Tenant', 'File', 'Records', 'Status', 'Amount', 'Submitted'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {(d.recent_jobs || []).map(j => (
                    <tr key={j.job_id} style={s.tr}>
                      <td style={{ ...s.td, fontWeight: 500, color: NAVY }}>{j.company_name}</td>
                      <td style={{ ...s.td, color: '#64748b', fontSize: 12, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{j.file_name}</td>
                      <td style={s.td}>{(j.record_count || 0).toLocaleString()}</td>
                      <td style={s.td}><StatusBadge status={j.status} /></td>
                      <td style={{ ...s.td, color: j.amount_usd ? '#059669' : '#94a3b8', fontWeight: j.amount_usd ? 600 : 400 }}>{j.amount_usd ? fmt$(j.amount_usd) : '—'}</td>
                      <td style={{ ...s.td, color: '#94a3b8', fontSize: 12 }}>{fmtDate(j.created_on_utc)}</td>
                    </tr>
                  ))}
                  {(d.recent_jobs || []).length === 0 && (
                    <tr><td colSpan={6} style={{ ...s.td, textAlign: 'center', color: '#94a3b8', padding: 32 }}>No recent jobs</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

const s = {
  page:       { display: 'flex', minHeight: '100vh', fontFamily: "'Inter', system-ui, sans-serif" },
  main:       { flex: 1, background: '#f0f5fb', padding: '36px 40px', overflowY: 'auto' },
  header:     { marginBottom: 20 },
  title:      { color: NAVY, fontSize: 26, fontWeight: 700, margin: 0, letterSpacing: '-0.4px' },
  sub:        { color: '#64748b', fontSize: 13, margin: '4px 0 0' },
  rangeBar:   { display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 16px', marginBottom: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  presets:    { display: 'flex', gap: 6 },
  presetBtn:  { borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 500, padding: '5px 12px', transition: 'all 0.12s' },
  customDates:{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' },
  dateLabel:  { color: '#94a3b8', fontSize: 12, fontWeight: 500 },
  dateInput:  { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 7, color: NAVY, fontSize: 12, padding: '5px 10px', outline: 'none' },
  statsRow:   { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 16 },
  card:       { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
  cardHead:   { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #e2e8f0' },
  cardTitle:  { color: NAVY, fontWeight: 600, fontSize: 14 },
  viewBtn:    { background: 'transparent', border: 'none', color: BLUE, cursor: 'pointer', fontSize: 13, fontWeight: 500 },
  table:      { width: '100%', borderCollapse: 'collapse' },
  th:         { color: '#94a3b8', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '10px 16px', textAlign: 'left', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
  tr:         { borderBottom: '1px solid #f1f5f9' },
  td:         { color: '#374151', fontSize: 13, padding: '12px 16px', verticalAlign: 'middle' },
};
